import { useState, useMemo } from 'react'
import moment from 'moment'
import { message } from 'antd'
import { GetT3CCallRecordDetailAPI } from '@services/quality-inspection'
import { GetMeetingCallRecordDetailAPI } from '@services/customer'
import { CallEventTextEnum, CallEventEnum, T3CCallRecordDetailType, SpeechTextItemType } from '@types'
import { getStaffName } from '@library'
import { TableIdEnum } from '@configs/table'
import { cosGetObject } from '../../../library/cosUtils'
import { cloneDeep, get } from 'lodash'

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?$/

export const useCallRecordDetail = (pageID: TableIdEnum.TCCC_CALL_RECORD | TableIdEnum.MEETING_CALL_RECORD = TableIdEnum.TCCC_CALL_RECORD) => {
  const [callRecordDetail, setCallRecordDetail] = useState<Partial<T3CCallRecordDetailType>>()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const isT3C = useMemo(() => pageID === TableIdEnum.TCCC_CALL_RECORD, [pageID])

  /**
   * 将blob转换为文本的格式
   * @param blob Blob对象
   */
  const blobToText = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result)
        } else {
          reject(new Error('Failed to read blob as text'))
        }
      }
      reader.onerror = reject
      reader.readAsText(blob)
    })
  }

  /**
   * 获取语音转文本信息
   * @param key cos文件key
   * @param startTime 通话开始时间 - 振铃时间【T3C通话记录必传】
   */
  const getText = async (key: string, startTime: string) => {
    let data: SpeechTextItemType[] = null
    try {
      const [blob] = await cosGetObject(key)
      if (!blob) {
        return []
      }
      const text = await blobToText(blob)
      data = JSON.parse(text)
    } catch (error) {
    }
    if (!data) return []
    // 腾会通话记录
    if (!isT3C) {
      data.forEach((item, index) => {
        item.isCustomerService = !item.User.startsWith('电话用户')
        item.startDiffSeconds = moment.duration(item.MeetingStart).asSeconds()
        item.endDiffSeconds = moment.duration(data[index + 1]?.MeetingStart ?? item.MeetingStart).asSeconds()
      })
      return data ?? []
    }
    // T3C通话记录
    data.forEach((item: SpeechTextItemType) => {
      const sTime = moment(item.Start)
      const eTime = moment(item.End)
      item.isCustomerService = emailRegex.test(item.User)
      item.startStandardTime = sTime.format('YYYY-MM-DD HH:mm:ss')
      item.endStandardTime = eTime.format('YYYY-MM-DD HH:mm:ss')
      item.startDiffSeconds = sTime.diff(startTime, 'seconds')
      item.endDiffSeconds = eTime.diff(startTime, 'seconds')
      const duration = moment.duration(sTime.diff(startTime))
      item.diffTime = [duration.hours(), duration.minutes(), duration.seconds()].reduce((pre, cur, index) => {
        return pre + String(cur).padStart(2, '0') + (index === 2 ? '' : ':')
      }, '')
    })
    return data ?? []
  }

  /**
   * 获取T3C通话记录详情
   * @param recordId 通话记录ID
   * @param detail 通话记录详情 - 包含一些简单信息。通过列表接口获取到的
   * @param isGetText 是否获取语音转文本信息
   */
  const getCallRecordDetail = async (recordId, detail: any = {}, isGetText: boolean = false) => {
    setLoading(true)
    const API = isT3C ? GetT3CCallRecordDetailAPI : GetMeetingCallRecordDetailAPI
    const [res, err] = await API(Number(recordId))
    const newDetail = cloneDeep({ ...detail, ...(res || {}) })
    setLoading(false)
    if (err) {
      setCallRecordDetail(newDetail)
      message.error(err?.message || err.rawError?.message || '请稍后刷新重试！')
      setOpen(true)
      return
    }
    if (isT3C) {
      // 对时间轴数据做处理
      // 通话记录的呼出时间
      let startTime = get(newDetail, 'callEventList.0.timestamp', 0)
      startTime = moment(Number(startTime) * 1000).format('YYYY-MM-DD HH:mm:ss')
      /* eslint-disable */
      newDetail?.callEventList?.forEach((item) => {
        // 对事件文本做处理【时间轴右侧的文本】 ------ start ------
        let eventText = `${CallEventTextEnum[item.event]}`
        if ([CallEventEnum.Start, CallEventEnum.StaffEnded].includes(item.event)) {
          eventText = `${getStaffName(newDetail.salesName, newDetail.salesNameCh)} ${eventText}`
        } else if ([CallEventEnum.ClientUserRinging, CallEventEnum.ClientUserAccepted].includes(item.event)) {
          eventText = `${newDetail.phone} ${eventText}`
        }
        item.eventText = eventText
        // 对事件文本做处理【时间轴右侧的文本】 ------ end ------
        // 将通话事件的开始时间(timestamp)转换为标准时间 ------ start ------
        const standardTime = moment(Number(item?.timestamp ?? 0) * 1000).format('YYYY-MM-DD HH:mm:ss')
        item.standardTime = standardTime
        // 将通话事件的开始时间(timestamp)转换为标准时间 ------ end ------
        // 每段时间轴的开始时间
        const timeLineStartTime = moment(standardTime)
        // 每段时间轴的开始时间 和 呼出时间 的 时间差
        const duration = moment.duration(timeLineStartTime.diff(startTime))
        // 计算音频的最大时长
        if (item.event === CallEventEnum.End) {
          newDetail.audioMaxS = duration.hours() * 3600 + duration.minutes() * 60 + duration.seconds()
        }
        // 将 时间差 转换为 HH:mm:ss 格式
        item.diffTime = [duration.hours(), duration.minutes(), duration.seconds()].reduce((pre, cur, index) => {
          return pre + String(cur).padStart(2, '0') + (index === 2 ? '' : ':')
        }, '')
      })
    }
    /* eslint-enable */
    if (newDetail.asrUrl && isGetText) {
      try {
        // callStartTime 只有T3C通话记录才有，腾会通话记录不会用到
        let callStartTime = get(newDetail, 'callEventList.1.timestamp', 0)
        callStartTime = moment(Number(callStartTime) * 1000).format('YYYY-MM-DD HH:mm:ss')
        newDetail.speechTextList = await getText(newDetail.asrUrl, callStartTime)
        // 对于非T3C通话记录，因为没有时间轴，所以将语音文本的最后一条消息的时间作为 audioMaxS
        if (!isT3C && newDetail.speechTextList.length) {
          newDetail.audioMaxS = moment.duration(newDetail.speechTextList.at(-1).MeetingStart).asSeconds()
        }
      } catch (error) {
        console.error('语音转文本失败！')
      }
    }
    setCallRecordDetail(newDetail)
    setOpen(true)
  }

  return ({ callRecordDetail, setCallRecordDetail, getCallRecordDetail, open, setOpen, loading })
}
